import { Box, Grid, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLazyQuery, useMutation, useQuery } from '@parkchirp/api-client';
import { useCallback, useContext, useEffect, useState } from 'react';

import { CommonContext } from '../../contexts';
import {
	CREATE_HOURLY_ACCOUNT_USER_GROUP_V2,
	GET_HOURLY_ACCOUNT_USER_GROUP_V2,
	GET_HOURLY_ACCOUNT_V2,
	UPDATE_HOURLY_ACCOUNT_USER_GROUP_V2,
} from '../../queries';
import {
	HourlyAccount,
	HourlyAccountUserGroup,
	HourlyAccountUserGroupZonePromoCode,
	PermissionFunctionGroup,
} from '../../types';
import { HourlyAccountAutocomplete } from '../inputs';
import { EditorModal } from '../modals';
import { PortalFeaturesEditor } from './portalFeaturesEditor';
import { PortalValidationsTable } from './portalValidationsTable';

export interface PortalUserGroupDetailsProps {
	hourlyAccount?: HourlyAccount;
	accountUserGroupId?: string;
	afterSave: () => void;
}

export const PortalUserGroupDetails = ({
	hourlyAccount,
	accountUserGroupId,
	afterSave,
}: PortalUserGroupDetailsProps) => {
	const theme = useTheme();
	const { apolloHeaders, organizationId, setShowSnackbar, setShowDialog } = useContext(CommonContext);

	const [model, setModel] = useState<HourlyAccountUserGroup>({ masterPolicy: false, hourlyAccount });
	const [parentValidations, setParentValidations] = useState<HourlyAccountUserGroupZonePromoCode[] | null>(null);
	const [parentFeatures, setParentFeatures] = useState<PermissionFunctionGroup[] | null>(null);

	const [hasChanges, setHasChanges] = useState(!accountUserGroupId);
	const [hasErrors, setHasErrors] = useState(false);

	const accountLocked = !!accountUserGroupId || !!hourlyAccount;

	const handleFieldChanges = useCallback(
		(modification) => {
			setModel({ ...model, ...modification });
			setHasChanges(true);
		},
		[model, setModel, setHasChanges],
	);

	const {
		data,
		loading,
		error: loadingError,
	} = useQuery(GET_HOURLY_ACCOUNT_USER_GROUP_V2, {
		variables: {
			id: accountUserGroupId,
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
			appHeaders: apolloHeaders,
		},
		fetchPolicy: 'network-only',
		ssr: false,
		skip: !accountUserGroupId,
	});

	const [getParentData, { error: parentLoadingError }] = useLazyQuery(GET_HOURLY_ACCOUNT_V2, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
			appHeaders: apolloHeaders,
		},
		fetchPolicy: 'network-only',
		ssr: false,
		onCompleted: (parentData) => {
			const masterPolicy = parentData.getHourlyAccount.hourlyAccountUserGroups.find(
				(userGroup) => userGroup.masterPolicy,
			);

			const masterPolicyValidations = masterPolicy.hourlyAccountUserGroupZonePromoCodes;
			const masterPolicyFeatures = masterPolicy.userGroup.functionGroups;

			if (!accountUserGroupId) {
				handleFieldChanges({
					hourlyAccountUserGroupZonePromoCodes: masterPolicyValidations,
					userGroup: {
						...model.userGroup,
						functionGroups: masterPolicyFeatures,
					},
				});
			}
			setParentValidations(masterPolicyValidations);
			setParentFeatures(masterPolicyFeatures);
		},
	});

	const [createHourlyAccountUserGroup, { loading: createLoading }] = useMutation(
		CREATE_HOURLY_ACCOUNT_USER_GROUP_V2,
		{
			context: {
				apiVersion: 'v2',
				appName: process.env.NEXT_PUBLIC_APP_NAME,
				appHeaders: apolloHeaders,
			},
		},
	);

	const [updateHourlyAccountUserGroup, { loading: updateLoading }] = useMutation(
		UPDATE_HOURLY_ACCOUNT_USER_GROUP_V2,
		{
			context: {
				apiVersion: 'v2',
				appName: process.env.NEXT_PUBLIC_APP_NAME,
				appHeaders: apolloHeaders,
			},
		},
	);

	useEffect(() => {
		if (!loading && data) {
			const { getHourlyAccountUserGroup: userGroup } = data;
			setModel(userGroup);

			if (!userGroup?.masterPolicy) {
				getParentData({
					variables: {
						id: data.getHourlyAccountUserGroup.hourlyAccount?.id,
					},
				});
			}
		}
	}, [loading, data, getParentData]);

	useEffect(() => {
		if (hourlyAccount) {
			getParentData({
				variables: {
					id: hourlyAccount.id,
				},
			});
		}
	}, [hourlyAccount, getParentData]);

	const handleValidate = useCallback(() => {
		if (!model.userGroup?.name) {
			setHasErrors(true);
			return false;
		}

		return true;
	}, [model]);

	const handleClose = useCallback(() => {
		setShowDialog(null);
	}, [setShowDialog]);
	const handleSave = useCallback(() => {
		if (!handleValidate()) {
			return;
		}

		const handlers = {
			onCompleted: () => {
				setShowSnackbar({
					message: `Hourly account user group ${!accountUserGroupId ? 'created' : 'updated'} successfully`,
					severity: 'success',
				});
				handleClose();
				afterSave();
			},
			onError: (apolloError) => {
				setShowSnackbar({
					message: apolloError.message,
					severity: 'error',
				});
			},
		};

		const input = {
			name: model.userGroup.name,
			accountId: model.hourlyAccount?.id,
			masterPolicy: model.masterPolicy ?? false,
			features: model.userGroup?.functionGroups?.map((functionGroups) => functionGroups.functionGroup.id) ?? [],
			validations:
				model.hourlyAccountUserGroupZonePromoCodes?.map((zonePromoCode) => ({
					id: zonePromoCode.id,
					zonePromoCodeId: zonePromoCode.zonePromoCode.id,
					webValidation: zonePromoCode.webValidation,
					qrValidation: zonePromoCode.qrValidation,
				})) ?? [],
		};

		if (!accountUserGroupId) {
			createHourlyAccountUserGroup({
				variables: {
					input,
				},
				...handlers,
			});
		} else {
			updateHourlyAccountUserGroup({
				variables: {
					input: {
						id: accountUserGroupId,
						...input,
					},
				},
				...handlers,
			});
		}
	}, [
		model,
		accountUserGroupId,
		updateHourlyAccountUserGroup,
		createHourlyAccountUserGroup,
		handleClose,
		handleValidate,
		afterSave,
		setShowSnackbar,
	]);

	useEffect(() => {
		if (loadingError || parentLoadingError) {
			setShowSnackbar({
				message: 'Could not load hourly account user group data',
				severity: 'error',
			});
			handleClose();
		}
	}, [loadingError, parentLoadingError, setShowSnackbar, handleClose]);

	const isLoading = loading || createLoading || updateLoading;

	return (
		<EditorModal
			theme={theme}
			handleClose={handleClose}
			handleSave={handleSave}
			headerText={`${!accountUserGroupId ? 'Create' : 'Edit'} Hourly Portal User Group`}
			hasUnsavedChanges={hasChanges}
			hasValidationErrors={hasErrors}
			loading={isLoading}
		>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<h3 style={{ marginBottom: '10px' }}>General</h3>
					<Grid container spacing={2}>
						<Grid item xs={4}>
							<HourlyAccountAutocomplete
								required
								label='Hourly Account'
								disabled={accountLocked}
								organizationId={organizationId}
								hourlyAccountId={model.hourlyAccount?.id}
								hourlyAccountPid={model.hourlyAccount?.pid}
								hourlyAccountName={model.hourlyAccount?.name}
								onChange={(hourlyAccountData) => {
									if (hourlyAccountData?.id) {
										getParentData({
											variables: {
												id: hourlyAccountData?.id,
											},
										});
									}
									handleFieldChanges({
										hourlyAccount: hourlyAccountData,
										hourlyAccountUserGroupZonePromoCodes: null,
									});
									setParentValidations(null);
									setParentFeatures(null);
								}}
								error={hasErrors && !model.hourlyAccount?.id}
								size='small'
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								size='small'
								label='User Group Name'
								variant='outlined'
								InputLabelProps={{ shrink: true }}
								onChange={(event) =>
									handleFieldChanges({
										userGroup: { ...model.userGroup, name: event.target.value },
									})
								}
								value={model.userGroup?.name ?? ''}
								error={hasErrors && !model.userGroup?.name}
								required
							/>
						</Grid>
					</Grid>
				</Grid>

				<Grid item xs={12}>
					<h3 style={{ marginBottom: '10px' }}>Access Control</h3>
					<Box>
						<h4 style={{ marginBottom: '10px' }}>Validations</h4>
						{!model.hourlyAccount ? (
							`Select an account to see available validations.`
						) : (
							<PortalValidationsTable
								facilityId={model.hourlyAccount?.facility?.id}
								zonePromoCodes={model.hourlyAccountUserGroupZonePromoCodes ?? []}
								parentZonePromoCodes={parentValidations}
								canEdit
								canAdd={model?.masterPolicy}
								onChange={(validations) =>
									handleFieldChanges({
										hourlyAccountUserGroupZonePromoCodes: validations,
									})
								}
							/>
						)}
						<h4 style={{ marginTop: '10px', marginBottom: '10px' }}>Features</h4>
						{!model.hourlyAccount ? (
							`Select an account to see available features.`
						) : (
							<PortalFeaturesEditor
								functionGroups={model.userGroup?.functionGroups}
								parentFunctionGroups={parentFeatures}
								canEdit
								canAdd={model?.masterPolicy}
								onChange={(functionGroups) => {
									handleFieldChanges({
										userGroup: {
											...model.userGroup,
											functionGroups,
										},
									});
								}}
							/>
						)}
					</Box>
				</Grid>
			</Grid>
		</EditorModal>
	);
};
